import type { ActionLogEntry } from "../types";
import { emitAction, TERMINAL_ORIGIN_TAG } from "./actionBridge";
import { makeId } from "../util/id";

export interface TerminalMiddlewareOptions {
  /** Channel id linking this middleware to a terminal instance. Default "default". */
  channel?: string;
  /** Return false to skip logging a given action. */
  filter?: (action: { type: string }) => boolean;
  /** Max characters of the JSON payload preview kept per entry. Default 500. */
  maxPayloadChars?: number;
}

/**
 * Redux middleware that streams every dispatched action into the terminal's
 * action feed. Framework-agnostic — typed loosely so it drops into a plain
 * Redux store or a Redux Toolkit store without importing either.
 *
 *   const store = configureStore({
 *     reducer,
 *     middleware: (gDM) => gDM().concat(createTerminalMiddleware()),
 *   });
 */
export function createTerminalMiddleware(
  options: TerminalMiddlewareOptions = {},
) {
  const channel = options.channel ?? "default";
  const maxChars = options.maxPayloadChars ?? 500;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (_store: any) =>
    (next: (action: any) => any) =>
    (action: any) => {
      const result = next(action);

      if (
        action &&
        typeof action.type === "string" &&
        (!options.filter || options.filter(action))
      ) {
        const { type, [TERMINAL_ORIGIN_TAG]: origin, ...rest } = action;
        const entry: ActionLogEntry = {
          id: makeId("act"),
          type,
          payload: previewPayload(rest, maxChars),
          createdAt: Date.now(),
          origin: origin === "terminal" ? "terminal" : "app",
        };
        emitAction(channel, entry);
      }

      return result;
    };
}

function previewPayload(rest: Record<string, unknown>, maxChars: number): unknown {
  const source =
    "payload" in rest && Object.keys(rest).length === 1 ? rest.payload : rest;
  try {
    const json = JSON.stringify(source);
    if (json === undefined) return undefined;
    return json.length > maxChars ? json.slice(0, maxChars) + "…" : source;
  } catch {
    return "[unserializable]";
  }
}
